/**
 * Synonym suggestions (features/synonym_suggestions.md) — pure client logic.
 *
 * The server computes suggestions at generation time and re-filters them live
 * on every read (shared/synonym_suggest.py). The editor ALSO filters locally,
 * because an Add in the Define Synonyms card changes `resume.synonyms` before
 * it is saved — the pair must disappear the moment the user clicks Add, and a
 * Decline must hide it before the round-trip returns.
 *
 * Normalization mirrors the backend exactly (shared/synonyms._tokenize):
 * lowercase, tokens of [a-z0-9+#&.'], everything else (spaces, hyphens,
 * slashes) splits tokens, joined with one space. pair_key = the two normalized
 * terms sorted and joined with '||'.
 */
import type { SynonymSet, SynonymSuggestion, SynonymDecline } from '../types';

const TOKEN_RE = /[a-z0-9+#&.']+/g;

export function normalizeTerm(term: string): string {
  return ((term || '').toLowerCase().match(TOKEN_RE) || []).join(' ');
}

export function pairKey(a: string, b: string): string {
  return [normalizeTerm(a), normalizeTerm(b)].sort().join('||');
}

/** normalized member → set id (first set wins), ignoring sets with < 2 terms. */
function setIndex(sets: SynonymSet[] | undefined): Map<string, string> {
  const idx = new Map<string, string>();
  for (const s of sets || []) {
    const terms = (s.terms || []).map(t => t.trim()).filter(Boolean);
    if (terms.length < 2) continue;
    for (const t of terms) {
      const n = normalizeTerm(t);
      if (n && !idx.has(n)) idx.set(n, s.id);
    }
  }
  return idx;
}

/**
 * Live filter: drop pairs already in the same set, pairs whose terms sit in
 * two DIFFERENT sets (merging sets is the user's call), and declined pairs.
 * Re-targets: when exactly one term is already in a set, the suggestion
 * becomes "add the other term to that set".
 */
export function filterSuggestions(
  suggestions: SynonymSuggestion[] | undefined,
  sets: SynonymSet[] | undefined,
  declinedKeys: Iterable<string> = [],
): SynonymSuggestion[] {
  const idx = setIndex(sets);
  const blocked = new Set(declinedKeys);
  const out: SynonymSuggestion[] = [];
  const seen = new Set<string>();
  for (const s of suggestions || []) {
    if (!s || !s.resume_term || !s.job_term) continue;
    const key = s.pair_key || pairKey(s.resume_term, s.job_term);
    if (blocked.has(key) || seen.has(key)) continue;
    const ra = idx.get(normalizeTerm(s.resume_term));
    const jb = idx.get(normalizeTerm(s.job_term));
    if (ra && jb) continue;
    seen.add(key);
    out.push({ ...s, pair_key: key, target_set_id: ra || jb || null });
  }
  return out;
}

export function newSynonymSetId(): string {
  return `syn_${Math.random().toString(36).slice(2, 10).padEnd(8, '0')}`;
}

/**
 * Apply an accepted suggestion to the resume's synonym sets: append the
 * missing term to the target set, or create a new two-term set. Returns a NEW
 * array (never mutates). Idempotent — adding an already-present term is a no-op.
 */
export function applySuggestion(
  sets: SynonymSet[] | undefined,
  s: SynonymSuggestion,
  makeId: () => string = newSynonymSetId,
): SynonymSet[] {
  const current = [...(sets || [])];
  const target = s.target_set_id ? current.find(x => x.id === s.target_set_id) : undefined;
  if (target) {
    const have = new Set((target.terms || []).map(normalizeTerm));
    const add = [s.resume_term, s.job_term].filter(t => !have.has(normalizeTerm(t)));
    if (!add.length) return current;
    return current.map(x => (x.id === target.id ? { ...x, terms: [...x.terms, ...add] } : x));
  }
  return [...current, { id: makeId(), terms: [s.resume_term, s.job_term] }];
}

export function declinedKeySet(declines: SynonymDecline[] | undefined): Set<string> {
  return new Set((declines || []).map(d => d.pair_key).filter(Boolean));
}

/** The words the user sees for a suggestion: "customer facing → client facing". */
export function describeSuggestion(s: SynonymSuggestion, sets?: SynonymSet[]): string {
  const target = s.target_set_id ? (sets || []).find(x => x.id === s.target_set_id) : undefined;
  if (target) {
    const have = new Set((target.terms || []).map(normalizeTerm));
    const missing = have.has(normalizeTerm(s.resume_term)) ? s.job_term : s.resume_term;
    return `Add "${missing}" to your set ${target.terms.join(' // ')}`;
  }
  return `"${s.resume_term}" // "${s.job_term}"`;
}

export interface SuggestionActions {
  decline: (terms: [string, string]) => Promise<SynonymDecline[]>;
}

/**
 * Decline flow: hide the pair optimistically, persist, and roll back (return
 * the error) if the server refuses. `hide`/`unhide` mutate the caller's local
 * hidden-key set; the returned declines list is the server's truth.
 */
export async function declineSuggestion(
  s: SynonymSuggestion,
  actions: SuggestionActions,
  hide: (key: string) => void,
  unhide: (key: string) => void,
): Promise<{ ok: true; declines: SynonymDecline[] } | { ok: false; error: unknown }> {
  hide(s.pair_key);
  try {
    const declines = await actions.decline([s.resume_term, s.job_term]);
    return { ok: true, declines };
  } catch (error) {
    unhide(s.pair_key);
    return { ok: false, error };
  }
}

/** Remove-decline flow for the Settings list (no optimistic hide: a failed
 * remove must leave the row visible so the user can retry). */
export async function removeDecline(
  key: string,
  remove: (key: string) => Promise<SynonymDecline[]>,
): Promise<{ ok: true; declines: SynonymDecline[] } | { ok: false; error: unknown }> {
  try {
    return { ok: true, declines: await remove(key) };
  } catch (error) {
    return { ok: false, error };
  }
}
